/**
 * PSP/CRE Tool v2.1 - Aviation Assets Constants
 * 
 * Defines aviation infrastructure for medical evacuation:
 * - Airfields (fixed-wing capable)
 * - Helicopter Landing Zones (HLZ)
 * - Forward Arming and Refueling Points (FARP)
 * - Patient Holding Areas (PHA)
 * 
 * @fileoverview Aviation infrastructure for MEDEVAC operations
 * @module constants/aviationAssets
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 22, 2024
 * @version 2.3 - POOL-002: PL_ARMY_AVIATION pool added (March 16, 2026)
 *   NEW: PL_ARMY_AVIATION pool — Polish Land Forces Army aviation (Lotnictwo Wojsk Lądowych).
 *     Primary aircraft: W3_SOKOL (W-3PL Głuszec, 8 aircraft, 56th Air Base Inowrocław).
 *     Transitioning to AW149 (32 ordered July 2022, deliveries 2023–2029, 25 BKPow).
 *     requires_mou: false — Army-organic; no MOU for Polish line units.
 *     S-70i Black Hawk procurement postponed June 2024 (Ukraine war reassessment).
 *   NEW: NATIONAL_DEFAULT_POOL.POLISH = 'PL_ARMY_AVIATION'.
 *   Sources: 1 BLWL org; 25 BKPow AW149 deliveries Dec 2025; Breaking Defense Aug 2024;
 *     Janes Nov 2025; AJMedP-2.
 *
 * @version 2.2 - FR_ALAT pool + FRENCH default fix (March 15, 2026)
 *   BUG FIX: NATIONAL_DEFAULT_POOL.FRENCH was 'NATO_NH90' (requires_mou: true).
 *     ALAT (Aviation Légère de l'Armée de Terre) is Army-organic — French line units
 *     do NOT require an MOU for their own NH90 Caïman MEDEVAC fleet.
 *     Mapping FRENCH to a shared NATO pool with MOU=true was doctrinally wrong.
 *   NEW: FR_ALAT pool — NH90 Caïman TTH, Army-organic, requires_mou: false.
 *     63 NH90 delivered by Feb 2025; replaced Puma fleet as primary ALAT MEDEVAC.
 *     nations: ['FRENCH']. FRENCH retained in NATO_NH90.nations for multinational ops.
 *   FIXED: NATIONAL_DEFAULT_POOL.FRENCH = 'FR_ALAT' (was 'NATO_NH90').
 *   Source: DGA NH90 final delivery Feb 4, 2025; defense.gouv.fr ALAT; AJMedP-2.
 * @version 2.1 - icons.js Integration
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (TRANSPORT_ICONS, STATUS_ICONS)
 * - Added getAirfieldOptions(), getHLZOptions(), getFARPOptions(), getPHAOptions()
 * - Added validateAviationConfig() function
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 lessons
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TRANSPORT_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  
  // Transport icons
  AIRCRAFT: TRANSPORT_ICONS?.AIRCRAFT || '✈️',
  HELICOPTER: TRANSPORT_ICONS?.HELICOPTER || '🚁',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  LOCATION: TACTICAL_ICONS?.LOCATION || '📍'
};

// ============================================================================
// AIRFIELD CATEGORIES
// ============================================================================

export const AIRFIELD_CATEGORIES = {
  CLASS_A: {
    code: 'CLASS_A',
    name: 'Major Airfield',
    nato_designation: 'Main Operating Base (MOB)',
    icon: ICONS.AIRCRAFT,
    color: '#4CAF50',
    
    runway: {
      length_m: { min: 2500 },
      width_m: { min: 45 },
      surface: ['CONCRETE', 'ASPHALT'],
      weight_bearing: 'HEAVY'
    },
    
    capabilities: {
      c17_capable: true,
      c130_capable: true,
      night_operations: true,
      ifr_capable: true,
      precision_approach: true,
      fuel_available: true,
      maintenance: 'FULL',
      patient_staging: true
    },
    
    medical: {
      patient_staging_area: true,
      capacity_patients: 200,
      role_4_access: true,
      ambulance_parking: 20,
      pha_large: true
    },
    
    typical_location: '200+ km from FEBA',
    echelon_support: 'CORPS_THEATER',
    examples: ['Otopeni', 'Mihail Kogălniceanu', 'Ramstein'],
    
    educational: {
      key_point: 'Strategic evacuation hub - C-17 capable',
      teaching_note: 'Primary destination for strategic MEDEVAC',
      ukraine_relevance: 'Rzeszów (Poland) served as primary NATO hub'
    }
  },
  
  CLASS_B: {
    code: 'CLASS_B',
    name: 'Secondary Airfield',
    nato_designation: 'Forward Operating Base (FOB)',
    icon: ICONS.AIRCRAFT,
    color: '#FF9800',
    
    runway: {
      length_m: { min: 1500, max: 2500 },
      width_m: { min: 30 },
      surface: ['CONCRETE', 'ASPHALT', 'AM2_MATTING'],
      weight_bearing: 'MEDIUM'
    },
    
    capabilities: {
      c17_capable: false,
      c130_capable: true,
      night_operations: true,
      ifr_capable: 'LIMITED',
      precision_approach: false,
      fuel_available: true,
      maintenance: 'LIMITED',
      patient_staging: true
    },
    
    medical: {
      patient_staging_area: true,
      capacity_patients: 100,
      role_3_nearby: true,
      ambulance_parking: 10,
      pha_medium: true
    },
    
    typical_location: '100-200 km from FEBA',
    echelon_support: 'DIVISION_CORPS',
    examples: ['Câmpia Turzii', 'Boboc'],
    
    educational: {
      key_point: 'C-130 capable - tactical/operational evacuation',
      teaching_note: 'Links theater to strategic evacuation'
    }
  },
  
  CLASS_C: {
    code: 'CLASS_C',
    name: 'Tactical Airfield',
    nato_designation: 'Contingency Location',
    icon: ICONS.AIRCRAFT,
    color: '#F44336',
    
    runway: {
      length_m: { min: 900, max: 1500 },
      width_m: { min: 18 },
      surface: ['ASPHALT', 'AM2_MATTING', 'COMPACTED_EARTH'],
      weight_bearing: 'LIGHT'
    },
    
    capabilities: {
      c17_capable: false,
      c130_capable: 'LIMITED',
      night_operations: 'NVG_ONLY',
      ifr_capable: false,
      precision_approach: false,
      fuel_available: 'BLADDER',
      maintenance: 'NONE',
      patient_staging: true
    },
    
    medical: {
      patient_staging_area: true,
      capacity_patients: 50,
      role_2_nearby: true,
      ambulance_parking: 5,
      pha_small: true
    },
    
    typical_location: '50-100 km from FEBA',
    echelon_support: 'BRIGADE_DIVISION',
    examples: ['Forward strips', 'Highway segments'],
    
    educational: {
      key_point: 'Tactical only - limited C-130 assault landing',
      teaching_note: 'Emergency use, austere conditions',
      ukraine_lesson: 'Highway strips used extensively in Ukraine'
    }
  }
};

// ============================================================================
// HELICOPTER LANDING ZONE (HLZ) CATEGORIES
// ============================================================================

export const HLZ_CATEGORIES = {
  SMALL: {
    code: 'SMALL',
    name: 'Small HLZ',
    nato_size: 'Size 1',
    icon: ICONS.HELICOPTER,
    color: '#4CAF50',
    
    dimensions: {
      diameter_m: 25,
      approach_angle_degrees: 10,
      obstacles_clear_m: 50
    },
    
    aircraft: {
      simultaneous: 1,
      max_size: 'MEDIUM',
      ch47_capable: false
    },
    
    capabilities: {
      day_operations: true,
      night_nvg: true,
      night_lights: 'REQUIRED',
      ifr_capable: false,
      confined_area: true
    },
    
    medical: {
      patient_staging_capacity: 10,
      ambulance_access: true,
      stretcher_positions: 6,
      medevac_primary: true
    },
    
    setup: {
      personnel: 4,
      setup_time_hours: 2,
      equipment: ['Markers', 'Lights', 'Radio'],
      maintenance_per_day_hours: 1
    },
    
    typical_location: 'Near Role 1/Role 2',
    
    educational: {
      key_point: 'Single helicopter MEDEVAC pickup',
      teaching_note: 'Primary point of injury pickup site'
    }
  },
  
  MEDIUM: {
    code: 'MEDIUM',
    name: 'Medium HLZ',
    nato_size: 'Size 2-3',
    icon: ICONS.HELICOPTER,
    color: '#FF9800',
    
    dimensions: {
      diameter_m: 50,
      approach_angle_degrees: 8,
      obstacles_clear_m: 100
    },
    
    aircraft: {
      simultaneous: 2,
      max_size: 'HEAVY',
      ch47_capable: true
    },
    
    capabilities: {
      day_operations: true,
      night_nvg: true,
      night_lights: true,
      ifr_capable: false,
      confined_area: false
    },
    
    medical: {
      patient_staging_capacity: 30,
      ambulance_access: true,
      stretcher_positions: 20,
      medevac_primary: true,
      farp_collocated: 'POSSIBLE'
    },
    
    setup: {
      personnel: 8,
      setup_time_hours: 4,
      equipment: ['Markers', 'Lights', 'Radio', 'Fire extinguisher'],
      maintenance_per_day_hours: 2
    },
    
    typical_location: 'Near Role 2/Role 3',
    
    educational: {
      key_point: 'Multi-helicopter ops, CH-47 capable',
      teaching_note: 'Can support MASCAL evacuation'
    }
  },
  
  LARGE: {
    code: 'LARGE',
    name: 'Large HLZ / Heliport',
    nato_size: 'Size 4+',
    icon: ICONS.HELICOPTER,
    color: '#2196F3',
    
    dimensions: {
      diameter_m: 100,
      approach_angle_degrees: 6,
      obstacles_clear_m: 200
    },
    
    aircraft: {
      simultaneous: 4,
      max_size: 'HEAVY',
      ch47_capable: true,
      formation_landing: true
    },
    
    capabilities: {
      day_operations: true,
      night_nvg: true,
      night_lights: true,
      ifr_capable: 'LIMITED',
      confined_area: false,
      permanent: true
    },
    
    medical: {
      patient_staging_capacity: 100,
      ambulance_access: true,
      stretcher_positions: 60,
      pha_collocated: true,
      farp_collocated: true
    },
    
    setup: {
      personnel: 15,
      setup_time_hours: 8,
      equipment: ['Full lighting', 'FARP', 'Crash/rescue', 'Radio/ATC'],
      maintenance_per_day_hours: 4
    },
    
    typical_location: 'Adjacent to Role 3',
    
    educational: {
      key_point: 'Full heliport - MASCAL capable',
      teaching_note: 'Primary Role 3 helicopter interface'
    }
  },
  
  HASTY: {
    code: 'HASTY',
    name: 'Hasty/Emergency HLZ',
    nato_size: 'Improvised',
    icon: ICONS.WARNING,
    color: '#F44336',
    
    dimensions: {
      diameter_m: 20,
      approach_angle_degrees: 15,
      obstacles_clear_m: 25
    },
    
    aircraft: {
      simultaneous: 1,
      max_size: 'MEDIUM',
      ch47_capable: false
    },
    
    capabilities: {
      day_operations: true,
      night_nvg: 'EMERGENCY',
      night_lights: 'IMPROVISED',
      ifr_capable: false,
      confined_area: true
    },
    
    medical: {
      patient_staging_capacity: 4,
      ambulance_access: 'LIMITED',
      stretcher_positions: 4,
      medevac_emergency: true
    },
    
    setup: {
      personnel: 2,
      setup_time_minutes: 15,
      equipment: ['VS-17 panels', 'Smoke', 'Radio'],
      maintenance_per_day_hours: 0
    },
    
    typical_location: 'Point of injury/Forward',
    
    educational: {
      key_point: 'Emergency only - single use',
      teaching_note: 'Minimum safe pickup zone',
      ukraine_lesson: 'Ukraine uses hasty HLZs near trenches'
    }
  }
};

// ============================================================================
// FORWARD ARMING AND REFUELING POINT (FARP)
// ============================================================================

export const FARP_CATEGORIES = {
  FARP_LIGHT: {
    code: 'FARP_LIGHT',
    name: 'FARP Light',
    icon: '⛽',
    color: '#4CAF50',
    
    capabilities: {
      fuel_capacity_liters: 20000,
      refuel_points: 2,
      aircraft_serviced_per_hour: 4,
      ammunition: false,
      maintenance: 'NONE'
    },
    
    fuel_types: ['JP-8', 'JET-A1'],
    
    setup: {
      personnel: 8,
      vehicles: 2,
      setup_time_hours: 2,
      can_relocate: true
    },
    
    typical_support: 'MEDEVAC_SECTION',
    
    educational: {
      key_point: 'Extends MEDEVAC helicopter range',
      teaching_note: 'Critical for long-range evacuations'
    }
  },
  
  FARP_STANDARD: {
    code: 'FARP_STANDARD',
    name: 'FARP Standard',
    icon: '⛽',
    color: '#FF9800',
    
    capabilities: {
      fuel_capacity_liters: 80000,
      refuel_points: 4,
      aircraft_serviced_per_hour: 8,
      ammunition: true,
      maintenance: 'NONE'
    },
    
    fuel_types: ['JP-8', 'JET-A1', 'AVGAS'],
    
    setup: {
      personnel: 20,
      vehicles: 6,
      setup_time_hours: 4,
      can_relocate: true
    },
    
    typical_support: 'AVIATION_COMPANY',
    
    educational: {
      key_point: 'Standard aviation support',
      teaching_note: 'Supports attack/assault aviation as well'
    }
  },
  
  FARP_HEAVY: {
    code: 'FARP_HEAVY',
    name: 'FARP Heavy',
    icon: '⛽',
    color: '#2196F3',
    
    capabilities: {
      fuel_capacity_liters: 200000,
      refuel_points: 8,
      aircraft_serviced_per_hour: 16,
      ammunition: true,
      maintenance: 'BASIC'
    },
    
    fuel_types: ['JP-8', 'JET-A1', 'AVGAS'],
    
    setup: {
      personnel: 40,
      vehicles: 12,
      setup_time_hours: 8,
      can_relocate: false
    },
    
    typical_support: 'CAB_SUPPORT',
    
    educational: {
      key_point: 'Division-level aviation support',
      teaching_note: 'Major FARP supporting sustained operations'
    }
  }
};

// ============================================================================
// PATIENT HOLDING AREA (PHA)
// ============================================================================

export const PHA_CATEGORIES = {
  SMALL: {
    code: 'PHA_SMALL',
    name: 'PHA Small',
    location: 'HLZ',
    icon: ICONS.LOCATION,
    color: '#4CAF50',
    
    capacity: {
      stretcher_patients: 10,
      ambulatory_patients: 20,
      total_capacity: 30
    },
    
    infrastructure: {
      shelter: 'TENT',
      climate_control: false,
      power: '5KW_GENERATOR',
      lighting: true
    },
    
    medical: {
      oxygen: true,
      suction: true,
      monitoring: 'BASIC',
      resupply_frequency_hours: 12
    },
    
    personnel: {
      medics: 4,
      nurses: 1,
      support: 2,
      total: 7
    },
    
    setup_time_hours: 2,
    
    educational: {
      key_point: 'Basic patient staging at HLZ',
      teaching_note: 'Holds patients awaiting helicopter'
    }
  },
  
  MEDIUM: {
    code: 'PHA_MEDIUM',
    name: 'PHA Medium',
    location: 'AIRFIELD_HLZ',
    icon: ICONS.LOCATION,
    color: '#FF9800',
    
    capacity: {
      stretcher_patients: 30,
      ambulatory_patients: 50,
      total_capacity: 80
    },
    
    infrastructure: {
      shelter: 'TENT_HEATED',
      climate_control: 'HEATING',
      power: '30KW_GENERATOR',
      lighting: true
    },
    
    medical: {
      oxygen: true,
      suction: true,
      monitoring: 'ENHANCED',
      minor_treatment: true,
      resupply_frequency_hours: 8
    },
    
    personnel: {
      medics: 8,
      nurses: 3,
      medical_officer: 1,
      support: 4,
      total: 16
    },
    
    setup_time_hours: 4,
    
    educational: {
      key_point: 'Enhanced staging with treatment',
      teaching_note: 'Can provide minor treatment while waiting'
    }
  },
  
  LARGE: {
    code: 'PHA_LARGE',
    name: 'PHA Large / AMC',
    location: 'AIRFIELD',
    icon: ICONS.LOCATION,
    color: '#2196F3',
    
    capacity: {
      stretcher_patients: 100,
      ambulatory_patients: 150,
      total_capacity: 250
    },
    
    infrastructure: {
      shelter: 'EXPANDABLE_SHELTER',
      climate_control: 'FULL',
      power: '100KW_GENERATOR',
      lighting: true,
      communications: true
    },
    
    medical: {
      oxygen: true,
      suction: true,
      monitoring: 'FULL',
      treatment_capability: true,
      holding_time_hours: 24,
      resupply_frequency_hours: 6
    },
    
    personnel: {
      medics: 15,
      nurses: 8,
      medical_officers: 3,
      admin: 4,
      support: 10,
      total: 40
    },
    
    setup_time_hours: 8,
    
    educational: {
      key_point: 'Full aeromedical staging (AMC equivalent)',
      teaching_note: 'Major evacuation hub capability'
    }
  }
};

// ============================================================================
// AVIATION ASSET ALLOCATION BY ECHELON
// ============================================================================

export const AVIATION_ASSET_ALLOCATION = {
  BRIGADE: {
    icon: ICONS.YELLOW,
    airfields: {
      class_a: 0,
      class_b: 0,
      class_c: 1
    },
    hlz: {
      small: 4,
      medium: 1,
      large: 0,
      hasty: 'AS_NEEDED'
    },
    farp: {
      light: 1
    },
    pha: {
      small: 2,
      medium: 1,
      large: 0
    }
  },
  
  DIVISION: {
    icon: ICONS.ORANGE,
    airfields: {
      class_a: 0,
      class_b: 1,
      class_c: 3
    },
    hlz: {
      small: 12,
      medium: 3,
      large: 1,
      hasty: 'AS_NEEDED'
    },
    farp: {
      light: 3,
      standard: 1
    },
    pha: {
      small: 6,
      medium: 3,
      large: 1
    }
  },
  
  CORPS: {
    icon: ICONS.RED,
    airfields: {
      class_a: 1,
      class_b: 3,
      class_c: 9
    },
    hlz: {
      small: 36,
      medium: 9,
      large: 3,
      hasty: 'AS_NEEDED'
    },
    farp: {
      light: 9,
      standard: 3,
      heavy: 1
    },
    pha: {
      small: 18,
      medium: 9,
      large: 3
    }
  }
};

// ============================================================================
// UKRAINE 2022-2024 LESSONS
// ============================================================================

export const UKRAINE_AVIATION_LESSONS = {
  applicable: true,
  source: 'Ukraine 2022-2024 MEDEVAC operations',
  icon: ICONS.WARNING,
  
  hlz_lessons: [
    'Hasty HLZs used near trenches for emergency extraction',
    'Night operations became primary due to drone surveillance',
    'Pre-surveyed HLZs attacked - use unplanned sites',
    'Smoke/decoy HLZs used to draw fire'
  ],
  
  airfield_lessons: [
    'Ukrainian airfields targeted by missiles - dispersal critical',
    'Highway strips used as contingency airfields',
    'Poland became primary strategic evacuation hub',
    'C-130 operations shifted to night'
  ],
  
  farp_lessons: [
    'FARPs dispersed due to targeting',
    'Rapid relocation capability essential',
    'Camouflage and concealment critical'
  ],
  
  recommendations: {
    hlz_dispersion: 'Maintain 3x more HLZ sites than needed',
    night_ops_priority: 'Assume 70%+ operations at night',
    strategic_evacuation: 'Plan for evacuation to neighboring country'
  }
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get airfield options for UI dropdown
 * @returns {Array} Airfield options
 */
export const getAirfieldOptions = () => {
  return Object.entries(AIRFIELD_CATEGORIES).map(([key, cat]) => ({
    value: cat.code,
    label: cat.name,
    icon: cat.icon,
    color: cat.color,
    nato: cat.nato_designation,
    c130_capable: cat.capabilities.c130_capable === true,
    c17_capable: cat.capabilities.c17_capable === true,
    patient_capacity: cat.medical.capacity_patients,
    typical_location: cat.typical_location
  }));
};

/**
 * Get HLZ options for UI dropdown
 * @returns {Array} HLZ options
 */
export const getHLZOptions = () => {
  return Object.entries(HLZ_CATEGORIES).map(([key, cat]) => ({
    value: cat.code,
    label: cat.name,
    icon: cat.icon,
    color: cat.color,
    nato_size: cat.nato_size,
    simultaneous_aircraft: cat.aircraft.simultaneous,
    patient_capacity: cat.medical.patient_staging_capacity,
    ch47_capable: cat.aircraft.ch47_capable,
    setup_time: cat.setup.setup_time_hours ? `${cat.setup.setup_time_hours}h` : `${cat.setup.setup_time_minutes}min`
  }));
};

/**
 * Get FARP options for UI dropdown
 * @returns {Array} FARP options
 */
export const getFARPOptions = () => {
  return Object.entries(FARP_CATEGORIES).map(([key, cat]) => ({
    value: cat.code,
    label: cat.name,
    icon: cat.icon,
    color: cat.color,
    fuel_capacity_liters: cat.capabilities.fuel_capacity_liters,
    refuel_points: cat.capabilities.refuel_points,
    can_relocate: cat.setup.can_relocate
  }));
};

/**
 * Get PHA options for UI dropdown
 * @returns {Array} PHA options
 */
export const getPHAOptions = () => {
  return Object.entries(PHA_CATEGORIES).map(([key, cat]) => ({
    value: cat.code,
    label: cat.name,
    icon: cat.icon,
    color: cat.color,
    location: cat.location,
    total_capacity: cat.capacity.total_capacity,
    stretcher_capacity: cat.capacity.stretcher_patients,
    setup_time_hours: cat.setup_time_hours,
    personnel_required: cat.personnel.total
  }));
};

/**
 * Get echelon allocation options
 * @returns {Array} Echelon options
 */
export const getEchelonOptions = () => {
  return Object.entries(AVIATION_ASSET_ALLOCATION).map(([key, alloc]) => ({
    value: key,
    label: key,
    icon: alloc.icon,
    totalHLZ: (alloc.hlz.small || 0) + (alloc.hlz.medium || 0) + (alloc.hlz.large || 0),
    totalAirfields: (alloc.airfields.class_a || 0) + (alloc.airfields.class_b || 0) + (alloc.airfields.class_c || 0)
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get airfield category by code
 * @param {string} code - Airfield category code
 * @returns {Object|null} Airfield category
 */
export const getAirfieldCategory = (code) => {
  return AIRFIELD_CATEGORIES[code] || null;
};

/**
 * Get HLZ category by code
 * @param {string} code - HLZ category code
 * @returns {Object|null} HLZ category
 */
export const getHLZCategory = (code) => {
  return HLZ_CATEGORIES[code] || null;
};

/**
 * Get FARP category by code
 * @param {string} code - FARP category code
 * @returns {Object|null} FARP category
 */
export const getFARPCategory = (code) => {
  return FARP_CATEGORIES[code] || null;
};

/**
 * Get PHA category by code
 * @param {string} code - PHA category code
 * @returns {Object|null} PHA category
 */
export const getPHACategory = (code) => {
  return PHA_CATEGORIES[code] || null;
};

/**
 * Get aviation asset allocation for echelon
 * @param {string} echelon - Echelon name
 * @returns {Object} Aviation allocation
 */
export const getAviationAllocation = (echelon) => {
  return AVIATION_ASSET_ALLOCATION[echelon] || AVIATION_ASSET_ALLOCATION.BRIGADE;
};

/**
 * Calculate total HLZ patient staging capacity
 * @param {Object} hlzConfig - HLZ configuration
 * @returns {number} Total patient capacity
 */
export const calculateHLZCapacity = (hlzConfig) => {
  let total = 0;
  
  if (hlzConfig.small) {
    total += hlzConfig.small * HLZ_CATEGORIES.SMALL.medical.patient_staging_capacity;
  }
  if (hlzConfig.medium) {
    total += hlzConfig.medium * HLZ_CATEGORIES.MEDIUM.medical.patient_staging_capacity;
  }
  if (hlzConfig.large) {
    total += hlzConfig.large * HLZ_CATEGORIES.LARGE.medical.patient_staging_capacity;
  }
  
  return total;
};

/**
 * Calculate total PHA capacity
 * @param {Object} phaConfig - PHA configuration
 * @returns {number} Total patient capacity
 */
export const calculatePHACapacity = (phaConfig) => {
  let total = 0;
  
  if (phaConfig.small) {
    total += phaConfig.small * PHA_CATEGORIES.SMALL.capacity.total_capacity;
  }
  if (phaConfig.medium) {
    total += phaConfig.medium * PHA_CATEGORIES.MEDIUM.capacity.total_capacity;
  }
  if (phaConfig.large) {
    total += phaConfig.large * PHA_CATEGORIES.LARGE.capacity.total_capacity;
  }
  
  return total;
};

/**
 * Calculate total personnel required for aviation assets
 * @param {Object} config - Aviation configuration
 * @returns {Object} Personnel requirements
 */
export const calculateAviationPersonnel = (config) => {
  let hlzPersonnel = 0;
  let phaPersonnel = 0;
  let farpPersonnel = 0;
  
  // HLZ personnel
  if (config.hlz) {
    hlzPersonnel += (config.hlz.small || 0) * HLZ_CATEGORIES.SMALL.setup.personnel;
    hlzPersonnel += (config.hlz.medium || 0) * HLZ_CATEGORIES.MEDIUM.setup.personnel;
    hlzPersonnel += (config.hlz.large || 0) * HLZ_CATEGORIES.LARGE.setup.personnel;
  }
  
  // PHA personnel
  if (config.pha) {
    phaPersonnel += (config.pha.small || 0) * PHA_CATEGORIES.SMALL.personnel.total;
    phaPersonnel += (config.pha.medium || 0) * PHA_CATEGORIES.MEDIUM.personnel.total;
    phaPersonnel += (config.pha.large || 0) * PHA_CATEGORIES.LARGE.personnel.total;
  }
  
  // FARP personnel
  if (config.farp) {
    farpPersonnel += (config.farp.light || 0) * FARP_CATEGORIES.FARP_LIGHT.setup.personnel;
    farpPersonnel += (config.farp.standard || 0) * FARP_CATEGORIES.FARP_STANDARD.setup.personnel;
    farpPersonnel += (config.farp.heavy || 0) * FARP_CATEGORIES.FARP_HEAVY.setup.personnel;
  }
  
  return {
    hlz_personnel: hlzPersonnel,
    pha_personnel: phaPersonnel,
    farp_personnel: farpPersonnel,
    total_personnel: hlzPersonnel + phaPersonnel + farpPersonnel,
    icon: ICONS.GREEN
  };
};

/**
 * Calculate setup time for aviation assets
 * @param {Object} config - Aviation configuration
 * @returns {Object} Setup time requirements
 */
export const calculateSetupTime = (config) => {
  let maxSetupHours = 0;
  
  // Find longest setup time (parallel setup assumed)
  if (config.hlz?.large > 0) {
    maxSetupHours = Math.max(maxSetupHours, HLZ_CATEGORIES.LARGE.setup.setup_time_hours);
  }
  if (config.hlz?.medium > 0) {
    maxSetupHours = Math.max(maxSetupHours, HLZ_CATEGORIES.MEDIUM.setup.setup_time_hours);
  }
  if (config.pha?.large > 0) {
    maxSetupHours = Math.max(maxSetupHours, PHA_CATEGORIES.LARGE.setup_time_hours);
  }
  if (config.farp?.heavy > 0) {
    maxSetupHours = Math.max(maxSetupHours, FARP_CATEGORIES.FARP_HEAVY.setup.setup_time_hours);
  }
  
  return {
    minimum_setup_hours: maxSetupHours,
    recommended_hours: maxSetupHours * 1.5,
    icon: maxSetupHours > 6 ? ICONS.ORANGE : ICONS.GREEN
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate aviation configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateAviationConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Check for minimum HLZ
  const totalHLZ = (config.hlz?.small || 0) + (config.hlz?.medium || 0) + 
                   (config.hlz?.large || 0);
  
  if (totalHLZ === 0) {
    errors.push({
      field: 'hlz',
      message: 'At least one HLZ required for MEDEVAC operations',
      icon: ICONS.RED
    });
  }
  
  // Check for PHA support
  const totalPHA = (config.pha?.small || 0) + (config.pha?.medium || 0) + 
                   (config.pha?.large || 0);
  
  if (totalPHA === 0 && totalHLZ > 2) {
    warnings.push({
      field: 'pha',
      message: 'PHA recommended for operations with multiple HLZ',
      icon: ICONS.YELLOW
    });
  }
  
  // Check HLZ to PHA ratio
  if (totalHLZ > 0 && totalPHA > 0) {
    const hlzCapacity = calculateHLZCapacity(config.hlz);
    const phaCapacity = calculatePHACapacity(config.pha);
    
    if (phaCapacity < hlzCapacity * 0.5) {
      warnings.push({
        field: 'pha',
        message: 'PHA capacity may be insufficient for HLZ throughput',
        icon: ICONS.YELLOW
      });
    }
  }
  
  // Check FARP for extended range
  if (config.extended_range && !config.farp?.light && !config.farp?.standard) {
    warnings.push({
      field: 'farp',
      message: 'FARP recommended for extended-range operations',
      icon: ICONS.YELLOW
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if airfield code is valid
 * @param {string} code - Airfield code
 * @returns {boolean} True if valid
 */
export const isValidAirfieldCode = (code) => {
  return code in AIRFIELD_CATEGORIES;
};

/**
 * Check if HLZ code is valid
 * @param {string} code - HLZ code
 * @returns {boolean} True if valid
 */
export const isValidHLZCode = (code) => {
  return code in HLZ_CATEGORIES;
};

// ============================================================================
// MEDEVAC POOL OPTIONS BY NATIONAL STANDARD
// Phase 5.0: getDefaultMEDEVACPool / getMEDEVACPoolOptions
// Required by Tab3_Medical.jsx Section 3B.0 (Tactical MEDEVAC Configuration).
//
// Pool shape: { code, label, description, primary_aircraft, requires_mou, educational }
//   code            — pool identifier stored in tacticalEvacConfig.air_pool.pool_code
//   label           — display name in dropdown
//   description     — short descriptor shown below label
//   primary_aircraft — matches helicopterSpecs HELICOPTER_SPECS key (for spec lookup)
//   requires_mou    — true when the pool requires a NATO Memorandum of Understanding
//   educational     — helper text shown in FormHelperText under dropdown
// ============================================================================

// ENH-037: Exported so casualtyCalculator.js can resolve pool_code → primary_aircraft.
export const MEDEVAC_POOLS = {
  // ── Romanian pools ──────────────────────────────────────────────────────
  RO_FAR: {
    code: 'RO_FAR',
    label: 'FAR — Forțele Aeriene Române (Air Force MEDEVAC)',
    description: 'IAR 330L Puma MEDEVAC — Romanian Air Force organic MEDEVAC',
    primary_aircraft: 'IAR330',
    requires_mou: false,
    educational: 'Romanian Air Force MEDEVAC squadrons. Allocated to MEDEVAC on divisional request. Source: SMG directive 2019.',
    nations: ['ROMANIAN']
  },
  RO_SMURD: {
    code: 'RO_SMURD',
    label: 'SMURD — Serviciul Mobil de Urgență (Civilian MEDEVAC)',
    description: 'EC135 / EC145 — civilian SMURD integration (peacetime/hybrid ops)',
    primary_aircraft: 'H145M',
    requires_mou: true,
    educational: 'Civilian SMURD helicopters integrated under emergency protocols. Requires MOU with Interior Ministry. Not available in high-threat combat zones.',
    nations: ['ROMANIAN']
  },

  // ── US Army pools ────────────────────────────────────────────────────────
  US_CAB: {
    code: 'US_CAB',
    label: 'CAB MEDEVAC — Division Combat Aviation Brigade',
    description: 'UH-60L Black Hawk — dedicated MEDEVAC company, Division CAB',
    primary_aircraft: 'UH60',
    requires_mou: false,
    educational: 'DUSTOFF MEDEVAC company organic to Division CAB. 9-line MEDEVAC standard. Source: ATP 4-02.2.',
    nations: ['US']
  },
  US_CH47: {
    code: 'US_CH47',
    label: 'CH-47 Chinook — Mass casualty MEDEVAC',
    description: 'CH-47F — MASCAL lift, 24 litter capacity',
    primary_aircraft: 'CH47',
    requires_mou: false,
    educational: 'Used for MASCAL evacuation when standard MEDEVAC capacity is saturated. Requires coordination with Aviation Bn. Source: FM 4-02.',
    nations: ['US']
  },

  // ── NATO multinational pools ─────────────────────────────────────────────
  NATO_NH90: {
    code: 'NATO_NH90',
    label: 'NH90 — NATO Multinational MEDEVAC Pool',
    description: 'NH90 TTH — France, Germany, Italy, Netherlands',
    primary_aircraft: 'NH90',
    requires_mou: true,
    educational: 'Multinational NH90 pool under NATO MEDEVAC sharing agreement. Requires activation through JMCC. Source: AJMedP-2 §3.',
    nations: ['NATO', 'GERMAN', 'FRENCH', 'DUTCH', 'ITALIAN']
  },
  NATO_GENERIC: {
    code: 'NATO_GENERIC',
    label: 'NATO Generic MEDEVAC Pool (MOU)',
    description: 'Nationally-provided helicopters under NATO MEDEVAC MOU',
    primary_aircraft: 'UH60',
    requires_mou: true,
    educational: 'Generic NATO pool — actual aircraft type provided by contributing nation. Requires JMCC activation and MEDEVAC MOU. Source: AJP-4.10C.',
    nations: ['NATO']
  },

  // ── UK pools ─────────────────────────────────────────────────────────────
  UK_AAC: {
    code: 'UK_AAC',
    label: 'AAC — Army Air Corps MEDEVAC',
    description: 'AW101 Merlin / AW159 Wildcat — British Army MEDEVAC',
    primary_aircraft: 'AW101',
    requires_mou: false,
    educational: 'British Army MEDEVAC. AW101 for primary MEDEVAC; AW159 for CASEVAC. Source: ADP-4.0 (British).',
    nations: ['UK', 'BRITISH']
  },

  // ── German pools ─────────────────────────────────────────────────────────
  DE_HEER: {
    code: 'DE_HEER',
    label: 'Heeresfliegertruppe — German Army Aviation MEDEVAC',
    description: 'H145M LUH — Bundeswehr organic MEDEVAC',
    primary_aircraft: 'H145M',
    requires_mou: false,
    educational: 'German Army (Heer) organic MEDEVAC. H145M (LUH SOF variant in medical role). Source: ZDv 14/35.',
    nations: ['GERMAN']
  },

  // ── French ALAT pool ─────────────────────────────────────────────────────
  FR_ALAT: {
    code: 'FR_ALAT',
    label: 'ALAT — Aviation Légère de l\'Armée de Terre (NH90 Caïman)',
    description: 'NH90 TTH Caïman — French Army organic MEDEVAC (ALAT)',
    primary_aircraft: 'NH90',
    requires_mou: false,
    educational: 'ALAT is Army-organic — no MOU required for French line units. ' +
      'NH90 Caïman TTH: 63 delivered by Feb 4, 2025; replaced Puma fleet as primary MEDEVAC. ' +
      '10-stretcher MEDEVAC configuration. NATO 9-line request procedure applies. ' +
      'EC725 Caracal (4e RHFS) is SOF/CSAR only — not used for standard line-unit MEDEVAC. ' +
      'Source: DGA NH90 delivery Feb 2025; defense.gouv.fr; AJMedP-2 §3.',
    nations: ['FRENCH']
  },

  // ── Polish Army Aviation pool ─────────────────────────────────────────────
  // Lotnictwo Wojsk Lądowych (LWL) — Polish Land Forces Army aviation.
  // Primary unit: 1 Brygada Lotnictwa Wojsk Lądowych (1 BLWL), 56th Air Base, Inowrocław.
  //   Also: 25 Brygada Kawalerii Powietrznej (25 BKPow), Tomaszów Mazowiecki.
  // Current MEDEVAC platform: W-3PL Głuszec (8 aircraft, Army-organic, no MOU).
  // Incoming platform: AW149 (32 ordered Jul 2022, ~12 delivered by Dec 2025, full delivery
  //   through 2029; MEDEVAC is an explicit mission role). Operated by 25 BKPow.
  // S-70i Black Hawk: procurement POSTPONED June 2024 (Ukraine reassessment) — not expected.
  // Sources: Breaking Defense Aug 2024; Janes Nov 2025; Shephard / HeliHub 2016; AJMedP-2.
  PL_ARMY_AVIATION: {
    code: 'PL_ARMY_AVIATION',
    label: 'LWL — Lotnictwo Wojsk Lądowych (Polish Army Aviation)',
    description: 'W-3PL Głuszec / AW149 — Polish Land Forces organic MEDEVAC',
    primary_aircraft: 'W3_SOKOL',       // W-3PL Głuszec; AW149 entering service 2023–2029
    requires_mou: false,                 // Army-organic — no MOU for Polish line units
    educational: 'Polish Army MEDEVAC is organic to Land Forces (LWL). ' +
      'Current primary: W-3PL Głuszec (8 aircraft, 56th Air Base, Inowrocław, 1 BLWL). ' +
      'Transitioning to AW149 (32 ordered Jul 2022, ~12 delivered by Dec 2025, full fleet by 2029; ' +
      '25 BKPow, Tomaszów Mazowiecki). AW149 has explicit MEDEVAC role. ' +
      'S-70i Black Hawk procurement postponed Jun 2024 (Ukraine war reassessment). ' +
      'No MOU required for Polish line units. Source: 1 BLWL / 25 BKPow; AJMedP-2.',
    nations: ['POLISH']
  },

  // ── Legacy Soviet-era ────────────────────────────────────────────────────
  MI17_POOL: {
    code: 'MI17_POOL',
    label: 'Mi-17 MEDEVAC Pool (legacy/partner force)',
    description: 'Mi-17 Hip — legacy Eastern-bloc MEDEVAC',
    primary_aircraft: 'MI17',
    requires_mou: true,
    educational: 'Mi-17 in medical configuration. Used by Eastern-bloc partner forces. Requires interoperability MOU. Not a standard NATO asset.',
    nations: ['LEGACY_SOVIET']
  }
};

// National standard → default pool code mapping
const NATIONAL_DEFAULT_POOL = {
  ROMANIAN: 'RO_FAR',
  US:       'US_CAB',
  NATO:     'NATO_GENERIC',
  UK:       'UK_AAC',
  BRITISH:  'UK_AAC',
  GERMAN:   'DE_HEER',
  FRENCH:   'FR_ALAT',           // ALAT Army-organic NH90 Caïman — no MOU for French units
  POLISH:   'PL_ARMY_AVIATION',  // LWL Army-organic W-3PL/AW149 — no MOU for Polish units
  DUTCH:    'NATO_NH90',
  ITALIAN:  'NATO_NH90',
  LEGACY_SOVIET: 'MI17_POOL'
};

/**
 * Get the default MEDEVAC pool code for a national standard.
 * Used by Tab3 to initialise tacticalEvacConfig.air_pool.pool_code.
 *
 * @param {string} nationalStandard - e.g. 'ROMANIAN', 'NATO', 'US'
 * @returns {string} pool code
 */
export function getDefaultMEDEVACPool(nationalStandard) {
  const key = (nationalStandard || 'NATO').toUpperCase();
  return NATIONAL_DEFAULT_POOL[key] || 'NATO_GENERIC';
}

/**
 * Get array of MEDEVAC pool options available for a national standard.
 * Returns pools whose nations[] includes the standard, plus NATO_GENERIC always.
 * Used to populate the Air Pool dropdown in Tab3 Section 3B.0.
 *
 * @param {string} nationalStandard - e.g. 'ROMANIAN', 'NATO', 'US'
 * @returns {Array<{code, label, description, primary_aircraft, requires_mou, educational}>}
 */
export function getMEDEVACPoolOptions(nationalStandard) {
  const key = (nationalStandard || 'NATO').toUpperCase();
  const filtered = Object.values(MEDEVAC_POOLS).filter(pool =>
    pool.nations.includes(key) || pool.nations.includes('NATO')
  );
  // Always include NATO_GENERIC as fallback option; deduplicate
  const seen = new Set();
  const result = [];
  for (const p of filtered) {
    if (!seen.has(p.code)) { seen.add(p.code); result.push(p); }
  }
  if (!seen.has('NATO_GENERIC')) result.push(MEDEVAC_POOLS.NATO_GENERIC);
  return result;
}

// ============================================================================
// EXPORTS
// ============================================================================

const aviationAssetsDefault = {
  AIRFIELD_CATEGORIES,
  HLZ_CATEGORIES,
  FARP_CATEGORIES,
  PHA_CATEGORIES,
  AVIATION_ASSET_ALLOCATION,
  UKRAINE_AVIATION_LESSONS,
  
  // UI Options
  getAirfieldOptions,
  getHLZOptions,
  getFARPOptions,
  getPHAOptions,
  getEchelonOptions,
  
  // Helper functions
  getAirfieldCategory,
  getHLZCategory,
  getFARPCategory,
  getPHACategory,
  getAviationAllocation,
  calculateHLZCapacity,
  calculatePHACapacity,
  calculateAviationPersonnel,
  calculateSetupTime,
  
  // MEDEVAC pool (Phase 5.0)
  getDefaultMEDEVACPool,
  getMEDEVACPoolOptions,

  // Validation
  validateAviationConfig,
  isValidAirfieldCode,
  isValidHLZCode
};

export default aviationAssetsDefault;
